import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/lib/auth-context";
import {
  listAdminUsers,
  inviteAdminUser,
  setAdminUserRole,
  deleteAdminUser,
  resetAdminUserPassword,
  getMyRoles,
  bootstrapFirstAdmin,
  adminBootstrapStatus,
} from "@/lib/admin-users.functions";
import { restoreVictorDemoCompanies } from "@/lib/coach-portal.functions";
import { verifyCompanyUserLogin } from "@/lib/bolag-portal.functions";
import { Copy, KeyRound, Trash2, UserPlus, ShieldCheck, ShieldAlert, Sparkles, LogIn } from "lucide-react";

export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: "Användare — Inkubera" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminPage,
});

function AdminPage() {
  return (
    <AppShell>
      <AdminInner />
    </AppShell>
  );
}

function AdminInner() {
  const { user } = useAuth();
  const myRolesFn = useServerFn(getMyRoles);
  const bootstrapStatusFn = useServerFn(adminBootstrapStatus);

  const myRoles = useQuery({
    queryKey: ["my-roles", user?.id],
    queryFn: async () => {
      try {
        return await myRolesFn();
      } catch (e) {
        console.warn("getMyRoles failed", e);
        return { roles: [] as string[] };
      }
    },
    enabled: !!user,
    retry: false,
  });
  const bootstrap = useQuery({
    queryKey: ["admin-bootstrap"],
    queryFn: () => bootstrapStatusFn(),
    retry: false,
  });

  if (myRoles.isLoading || bootstrap.isLoading) {
    return <div className="p-8 text-sm text-muted-foreground">Laddar…</div>;
  }

  const isAdmin = (myRoles.data?.roles ?? []).includes("admin");

  if (!bootstrap.data?.hasAdmin) {
    return <BootstrapPanel />;
  }

  if (!isAdmin) {
    return (
      <div className="p-8 max-w-xl">
        <div className="rounded-2xl border border-border bg-card p-8 flex items-start gap-4">
          <ShieldAlert className="size-6 text-destructive shrink-0" />
          <div>
            <h1 className="font-display text-xl">Endast administratörer</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Du behöver admin-roll för att hantera användare. Be en befintlig
              admin om åtkomst.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <UsersPanel />;
}

function BootstrapPanel() {
  const fn = useServerFn(bootstrapFirstAdmin);
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [result, setResult] = useState<{ email?: string; tempPassword: string } | null>(
    null,
  );
  const m = useMutation({
    mutationFn: (data: { email: string; full_name: string }) =>
      fn({ data }),
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ["admin-bootstrap"] });
    },
  });

  return (
    <div className="p-8 max-w-xl">
      <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">
        Första uppstarten
      </div>
      <h1 className="font-display text-3xl">Skapa första admin-kontot</h1>
      <p className="text-sm text-muted-foreground mt-2 max-w-md">
        Det finns ännu ingen administratör. Skapa det första kontot här — sedan
        kan du bjuda in fler användare härifrån.
      </p>

      {result ? (
        <CredentialsCard email={result.email ?? email} password={result.tempPassword} />
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            m.mutate({ email: email.trim(), full_name: name.trim() });
          }}
          className="mt-6 space-y-4 rounded-2xl border border-border bg-card p-6"
        >
          <Field label="Namn">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder="Förnamn Efternamn"
            />
          </Field>
          <Field label="E-post">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="namn@inkubera.se"
            />
          </Field>
          {m.error && (
            <div className="text-sm text-destructive">{(m.error as Error).message}</div>
          )}
          <button
            disabled={m.isPending}
            className="h-10 px-5 rounded-lg bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {m.isPending ? "Skapar…" : "Skapa admin-konto"}
          </button>
        </form>
      )}
    </div>
  );
}

function UsersPanel() {
  const listFn = useServerFn(listAdminUsers);
  const inviteFn = useServerFn(inviteAdminUser);
  const setRoleFn = useServerFn(setAdminUserRole);
  const deleteFn = useServerFn(deleteAdminUser);
  const resetFn = useServerFn(resetAdminUserPassword);
  const qc = useQueryClient();
  const { user } = useAuth();

  const list = useQuery({ queryKey: ["admin-users"], queryFn: () => listFn() });

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<"admin" | "member">("member");
  const [issued, setIssued] = useState<{ email: string; password: string } | null>(null);

  const invite = useMutation({
    mutationFn: (data: { email: string; full_name: string; role: "admin" | "member" }) =>
      inviteFn({ data }),
    onSuccess: (r) => {
      setIssued({ email: r.email ?? email, password: r.tempPassword });
      setEmail("");
      setName("");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  const changeRole = useMutation({
    mutationFn: (v: { user_id: string; role: "admin" | "member" }) =>
      setRoleFn({ data: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });

  const removeUser = useMutation({
    mutationFn: (user_id: string) => deleteFn({ data: { user_id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });

  const resetPw = useMutation({
    mutationFn: (v: { user_id: string; email: string }) =>
      resetFn({ data: { user_id: v.user_id } }).then((r) => ({ ...r, email: v.email })),
    onSuccess: (r) => setIssued({ email: r.email, password: r.tempPassword }),
  });

  return (
    <div className="p-8 space-y-8">
      <header>
        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">
          Administration
        </div>
        <h1 className="font-display text-3xl">Användare och roller</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Bjud in nya användare till portalen och styr deras behörighet.
        </p>
      </header>

      {issued && <CredentialsCard email={issued.email} password={issued.password} onClose={() => setIssued(null)} />}

      <section className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus className="size-4" />
          <h2 className="font-display text-lg">Bjud in ny användare</h2>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            invite.mutate({ email: email.trim(), full_name: name.trim(), role });
          }}
          className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end"
        >
          <Field label="Namn">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder="Förnamn Efternamn"
            />
          </Field>
          <Field label="E-post">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="namn@inkubera.se"
            />
          </Field>
          <Field label="Roll">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as "admin" | "member")}
              className="input"
            >
              <option value="member">Medlem</option>
              <option value="admin">Administratör</option>
            </select>
          </Field>
          <button
            disabled={invite.isPending}
            className="h-10 px-5 rounded-lg bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {invite.isPending ? "Skapar…" : "Skapa konto"}
          </button>
        </form>
        {invite.error && (
          <div className="mt-3 text-sm text-destructive">
            {(invite.error as Error).message}
          </div>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          Ett tillfälligt lösenord visas direkt efter att kontot skapats. Skicka det
          säkert till användaren — de kan logga in direkt.
        </p>
      </section>

      <DemoDataPanel />

      <CompanyLoginTestPanel />

      <section className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="p-6 pb-3 flex items-center gap-2">
          <ShieldCheck className="size-4" />
          <h2 className="font-display text-lg">Befintliga användare</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground border-y border-border">
              <tr>
                <th className="px-6 py-3">Namn</th>
                <th className="px-6 py-3">E-post</th>
                <th className="px-6 py-3">Roll</th>
                <th className="px-6 py-3 text-right">Åtgärder</th>
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((u) => {
                const isMe = u.id === user?.id;
                const current: "admin" | "member" = u.roles.includes("admin")
                  ? "admin"
                  : "member";
                return (
                  <tr key={u.id} className="border-b border-border last:border-0">
                    <td className="px-6 py-3">
                      {u.full_name || <span className="text-muted-foreground">—</span>}
                      {isMe && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                          (du)
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3">{u.email}</td>
                    <td className="px-6 py-3">
                      <select
                        disabled={isMe || changeRole.isPending}
                        value={current}
                        onChange={(e) =>
                          changeRole.mutate({
                            user_id: u.id,
                            role: e.target.value as "admin" | "member",
                          })
                        }
                        className="h-8 px-2 rounded-md bg-secondary text-sm"
                      >
                        <option value="member">Medlem</option>
                        <option value="admin">Administratör</option>
                      </select>
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() =>
                            resetPw.mutate({ user_id: u.id, email: u.email ?? "" })
                          }
                          className="h-8 px-2.5 rounded-md bg-secondary hover:bg-secondary/70 inline-flex items-center gap-1.5 text-xs"
                          title="Återställ lösenord"
                        >
                          <KeyRound className="size-3.5" /> Nytt lösenord
                        </button>
                        <button
                          disabled={isMe}
                          onClick={() => {
                            if (confirm(`Ta bort ${u.email}?`)) removeUser.mutate(u.id);
                          }}
                          className="h-8 px-2.5 rounded-md bg-destructive/10 text-destructive hover:bg-destructive/20 inline-flex items-center gap-1.5 text-xs disabled:opacity-30"
                          title="Ta bort användare"
                        >
                          <Trash2 className="size-3.5" /> Ta bort
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {list.data?.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-muted-foreground">
                    Inga användare ännu.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function CredentialsCard({
  email,
  password,
  onClose,
}: {
  email: string;
  password: string;
  onClose?: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-2xl border border-primary/40 bg-primary/5 p-6 mt-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
            Tillfälliga inloggningsuppgifter
          </div>
          <div className="font-display text-lg mt-1">Spara dessa nu</div>
          <p className="text-xs text-muted-foreground mt-1 max-w-md">
            Lösenordet visas bara denna gång. Skicka det säkert till användaren.
          </p>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground">
            Stäng
          </button>
        )}
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-lg bg-background border border-border p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">E-post</div>
          <div className="font-mono text-sm mt-1 break-all">{email}</div>
        </div>
        <div className="rounded-lg bg-background border border-border p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Lösenord</div>
          <div className="font-mono text-sm mt-1 break-all">{password}</div>
        </div>
      </div>
      <button
        onClick={() => {
          navigator.clipboard.writeText(`E-post: ${email}\nLösenord: ${password}`);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className="mt-4 h-9 px-3 rounded-lg bg-foreground text-background text-xs font-medium inline-flex items-center gap-1.5"
      >
        <Copy className="size-3.5" /> {copied ? "Kopierat!" : "Kopiera båda"}
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function DemoDataPanel() {
  const restoreFn = useServerFn(restoreVictorDemoCompanies);
  const m = useMutation({
    mutationFn: () => restoreFn(),
  });

  return (
    <section className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="size-4" />
        <h2 className="font-display text-lg">Demo-data</h2>
      </div>
      <p className="text-sm text-muted-foreground max-w-2xl">
        Återskapa demo-bolagen (Lumea Health, NorrCarbon, Stellaron Mobility) och
        koppla dem till Victor Sunnliden som primär coach. Använd om något har
        försvunnit eller blivit fel — det är säkert att köra flera gånger.
      </p>
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <button
          disabled={m.isPending}
          onClick={() => m.mutate()}
          className="h-10 px-5 rounded-lg bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          {m.isPending ? "Återskapar…" : "Återskapa demo-bolag för Victor"}
        </button>
        {m.isSuccess && (
          <span className="text-sm text-muted-foreground">
            Klart — {m.data.companies.join(", ")} kopplade till {m.data.coachEmail}.
          </span>
        )}
      </div>
      {m.error && (
        <div className="mt-3 text-sm text-destructive">{(m.error as Error).message}</div>
      )}
    </section>
  );
}

function CompanyLoginTestPanel() {
  const verifyFn = useServerFn(verifyCompanyUserLogin);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const m = useMutation({
    mutationFn: (input: { email: string; password: string }) => verifyFn({ data: input }),
  });

  return (
    <section className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center gap-2 mb-2">
        <LogIn className="size-4" />
        <h2 className="font-display text-lg">Testa bolagsinloggning</h2>
      </div>
      <p className="text-sm text-muted-foreground max-w-2xl">
        Snabbtest som verifierar att en bolagsanvändare kan logga in efter
        kontolänkning eller lösenordsåterställning. Sessionen loggas ut direkt
        — din egen inloggning påverkas inte.
      </p>
      <form
        className="mt-4 grid gap-3 sm:grid-cols-[1fr_1fr_auto] items-end"
        onSubmit={(e) => {
          e.preventDefault();
          if (!email || !password) return;
          m.mutate({ email: email.trim(), password });
        }}
      >
        <Field label="E-post">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="bolag@example.com"
            className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm"
          />
        </Field>
        <Field label="Tillfälligt lösenord">
          <input
            type="text"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Klistra in lösenordet"
            className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm font-mono"
          />
        </Field>
        <button
          disabled={m.isPending}
          className="h-10 px-5 rounded-lg bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          {m.isPending ? "Testar…" : "Testa inloggning"}
        </button>
      </form>

      {m.data && (
        <div
          className={
            "mt-4 rounded-lg border p-4 text-sm " +
            (m.data.ok
              ? "border-emerald-500/40 bg-emerald-500/5"
              : "border-destructive/40 bg-destructive/5")
          }
        >
          {m.data.ok ? (
            <div className="space-y-1">
              <div className="font-medium">
                ✓ Inloggning lyckades på {m.data.durationMs} ms
              </div>
              <div className="text-muted-foreground">
                Roller: {m.data.roles.length ? m.data.roles.join(", ") : "(inga)"}
              </div>
              <div className="text-muted-foreground">
                Bolag:{" "}
                {m.data.companies.length
                  ? m.data.companies.map((c) => c.name).join(", ")
                  : "(inga kopplade)"}
              </div>
            </div>
          ) : (
            <div>
              <div className="font-medium text-destructive">✗ Inloggning misslyckades</div>
              <div className="text-muted-foreground mt-1">{m.data.error}</div>
            </div>
          )}
        </div>
      )}

      {m.error && (
        <div className="mt-3 text-sm text-destructive">{(m.error as Error).message}</div>
      )}
    </section>
  );
}
